import PropTypes from "prop-types";
// form
import { useFormContext, Controller } from "react-hook-form";
// @mui
import {
  Select,
  Checkbox,
  MenuItem,
  TextField,
  InputLabel,
  FormControl,
  OutlinedInput,
  FormHelperText,
} from "@mui/material";
import { renderMultipler } from "src/utils/render";

// ----------------------------------------------------------------------

RHFSelect.propTypes = {
  name: PropTypes.string,
  native: PropTypes.bool,
  children: PropTypes.node,
  helperText: PropTypes.node,
  placeholder: PropTypes.string,
  maxHeight: PropTypes.number,
};

export function RHFSelect({
  name,
  native,
  children,
  options,
  idLabel = "name",
  idValue = "id",
  helperText,
  maxHeight = 220,
  styledVariant = "default",
  label,
  placeholder,
  containerSx,
  ...other
}) {
  const { control } = useFormContext();
  if (!styledVariant) {
    return (
      <Controller
        name={name}
        control={control}
        render={({ field, fieldState: { error } }) => (
          <TextField
            {...field}
            select
            fullWidth
            SelectProps={{
              native,
              MenuProps: {
                PaperProps: {
                  sx: {
                    ...(!native && {
                      px: 1,
                      maxHeight: typeof maxHeight === "number" ? maxHeight : "unset",
                      "& .MuiMenuItem-root": {
                        px: 1,
                        borderRadius: 0.75,
                        typography: "body2",
                        textTransform: "capitalize",
                      },
                    }),
                  },
                },
              },
              sx: { textTransform: "capitalize" },
            }}
            error={!!error}
            helperText={error ? error?.message : helperText}
            {...other}
          >
            {children}
            {!children && (
              <>
                <option value="" />
                {options?.map((option) => (
                  <option key={option[idValue]} value={option[idValue]}>
                    {option[idLabel]}
                  </option>
                ))}
              </>
            )}
          </TextField>
        )}
      />
    );
  }
  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <FormControl sx={containerSx}>
          {label && <InputLabel id={name}> {label} </InputLabel>}

          <Select
            {...field}
            value={field.value || ""}
            displayEmpty={!!placeholder}
            labelId={name}
            input={<OutlinedInput fullWidth label={label} error={!!error} />}
            placeholder={placeholder}
            MenuProps={{
              PaperProps: {
                sx: { px: 1, maxHeight: 280 },
              },
            }}
            {...other}
          >
            {options?.map((option) => {
              const selected = field.value === option[idValue];

              return (
                <MenuItem
                  key={option[idValue]}
                  value={option[idValue]}
                  sx={{
                    py: 1,
                    px: 2,
                    mt: 0.5,
                    borderRadius: 0.75,
                    typography: "body2",
                    ...(selected && {
                      fontWeight: "fontWeightMedium",
                    }),
                  }}
                >
                  {option[idLabel]}
                </MenuItem>
              );
            })}
          </Select>

          {(!!error || helperText) && (
            <FormHelperText error={!!error}>{error ? error?.message : helperText}</FormHelperText>
          )}
        </FormControl>
      )}
    />
  );
}

// ----------------------------------------------------------------------

RHFMultiSelect.propTypes = {
  name: PropTypes.string,
  chip: PropTypes.bool,
  label: PropTypes.string,
  options: PropTypes.array,
  checkbox: PropTypes.bool,
  placeholder: PropTypes.string,
  helperText: PropTypes.node,
  sx: PropTypes.object,
};

export function RHFMultiSelect({
  name,
  chip,
  label,
  options,
  idLabel = "name",
  idValue = "id",
  checkbox,
  placeholder,
  helperText,
  sx,
  ...other
}) {
  const { control } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <FormControl sx={sx}>
          {label && <InputLabel id={name}> {label} </InputLabel>}

          <Select
            {...field}
            value={field.value || []}
            multiple
            displayEmpty={!!placeholder}
            labelId={name}
            input={<OutlinedInput fullWidth label={label} error={!!error} />}
            renderValue={(values) => renderMultipler(values, options, { idLabel, idValue, chip })}
            MenuProps={{
              PaperProps: {
                sx: { px: 1, maxHeight: 280 },
              },
            }}
            {...other}
          >
            {placeholder && (
              <MenuItem
                disabled
                value=""
                sx={{
                  py: 1,
                  px: 2,
                  borderRadius: 0.75,
                  typography: "body2",
                }}
              >
                <em> {placeholder} </em>
              </MenuItem>
            )}

            {options?.map((option) => {
              const selected = field.value?.includes(option[idValue]);
              return (
                <MenuItem
                  key={option[idValue]}
                  value={option[idValue]}
                  sx={{
                    py: 1,
                    px: 2,
                    mt: 0.5,
                    borderRadius: 0.75,
                    typography: "body2",
                    ...(selected && {
                      fontWeight: "fontWeightMedium",
                    }),
                    ...(checkbox && {
                      p: 0.25,
                    }),
                  }}
                >
                  {checkbox && <Checkbox disableRipple size="small" checked={selected} />}

                  {option[idLabel]}
                </MenuItem>
              );
            })}
          </Select>

          {(!!error || helperText) && (
            <FormHelperText error={!!error}>{error ? error?.message : helperText}</FormHelperText>
          )}
        </FormControl>
      )}
    />
  );
}
